import IntegerTextField from 'common/components/IntegerTextField'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { fNumber } from 'common/utils/formatNumber'
import { errorMessageByField } from 'common/utils/hook-form'
import { numberSchema } from 'common/utils/yup'
import { endOfToday } from 'date-fns'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import startOfToday from 'date-fns/startOfToday'
import {
  useMutateCreateCycle,
  useMutateCreateCycleWithCredit,
  useMutateEditCycle,
} from 'modules/pond/api/mutation'
import {
  useGetBreed,
  useGetBreedBrand,
  useGetBreedProduct,
  useGetCycleById,
  useGetFeedPrograms,
  useGetMarkets,
} from 'modules/pond/api/queries'
import { useGetPondNameById, useSpeciesName } from 'modules/pond/api/selector'
import { PondTab, SpeciesName } from 'modules/pond/constants'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { useCreditWalletFeatureEnabled } from 'modules/wallets/hooks'
import { useFeatureEnabled } from 'core/feature-config/api/queries'
import { useSnackbar } from 'notistack'
import { Fragment, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import * as Yup from 'yup'

import LoadingButton from '@mui/lab/LoadingButton'
import { Button } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import editFill from '@iconify/icons-ant-design/edit-fill'
import plus from '@iconify/icons-ant-design/plus'
import { Icon } from '@iconify/react'

import FeedProgramDialog from '../FeedProgramDialog'

type EstimateDialogType = 'totalProduction' | 'totalMarket'
type EstimateDialogMode = 'create' | 'edit'

// Sentinel value for the "+ สร้างสูตรประเมิน" row at the bottom of the formula dropdown.
const CREATE_FEED_PROGRAM_OPTION = -2

interface PondCycleFormProps {
  formId: string
  pondId: string
  onClose?: () => void
  onDirtyChange?: (dirty: boolean) => void
  onSubmittingChange?: (submitting: boolean) => void
  showSaveButton?: boolean
}

interface FormDataType {
  startDate: Date
  endDate?: Date
  saltiness?: number
  breed: number
  breedBrand: number
  breedProduct: number
  amount?: number
  totalProduction: number
  totalMarket: number
  feedPercentOfFishWeight?: string
  initialSize?: string
}

interface DialogConfig {
  type: EstimateDialogType
  mode: EstimateDialogMode
  initialId?: number
  programId?: number
  isOpen: boolean
}

const PondCycleForm = ({
  formId,
  pondId,
  onClose,
  onDirtyChange,
  onSubmittingChange,
  showSaveButton = true,
}: PondCycleFormProps) => {
  const theme = useTheme()
  const { t } = useTranslation(['pond', 'common'])
  const today = startOfToday()
  const { enqueueSnackbar } = useSnackbar()

  const location = useLocation()
  // treat page as edit mode when route path ends with "/setting"
  const isEditMode = location.pathname.endsWith('/setting')

  const { data: cycleInfo, isFetching: cycleFetching } = useGetCycleById()
  const speciesName = useSpeciesName()

  const creditWalletEnabled = useCreditWalletFeatureEnabled()
  const createCycleMutation = useMutateCreateCycle()
  const createCycleWithCreditMutation = useMutateCreateCycleWithCredit()
  const editCycleMutation = useMutateEditCycle()

  const { data: pondInfo } = useGetPondNameById(pondId)

  const isFish = speciesName === SpeciesName.Fish

  const amountSchema = numberSchema(t('require.number'))

  const schema = Yup.object().shape({
    startDate: Yup.date(),
    endDate: Yup.date(),
    saltiness: Yup.number().required(t('please.input.saltiness')),
    breed: Yup.number().positive(t('please.input.breed')),
    breedBrand: Yup.number().positive(t('please.input.breed.brand')),
    breedProduct: Yup.number().positive(t('please.input.breed.product')),
    amount: Yup.number()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          const noCommas = originalValue.replace(/,/g, '')
          const parsed = Number(noCommas)
          return Number.isNaN(parsed) ? value : parsed
        }
        return value
      })
      .typeError(t('require.number'))
      .required(t('please.input.amount')),
    totalProduction: Yup.number().required(t('please.input.totalProduction')),
    totalMarket: Yup.number().required(t('please.input.income')),
    feedPercentOfFishWeight: amountSchema,
    initialSize: isFish ? amountSchema.required(t('please.input.initialSize')) : amountSchema,
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues,
  } = useForm<FormDataType>({
    defaultValues: {
      startDate: today,
      endDate: undefined,
      saltiness: undefined,
      breed: -1,
      breedBrand: -1,
      breedProduct: -1,
      amount: undefined,
      totalProduction: -1,
      totalMarket: -1,
      feedPercentOfFishWeight: undefined,
      initialSize: undefined,
    },
    resolver,
  })

  const { data: breeds, isLoading: isBreedLoading } = useGetBreed(pondInfo?.species.id)
  const { data: breedBrand, isLoading: isBreedBrandLoading } = useGetBreedBrand(watch('breed'))
  const { data: breedProduct, isLoading: isBreedProductLoading } = useGetBreedProduct(
    watch('breed'),
    watch('breedBrand')
  )
  const { data: totalProductions, isLoading: isTotalProductionsLoading } = useGetFeedPrograms()
  const { data: totalMarkets } = useGetMarkets(pondInfo?.species.id)
  const showUpdateBtn = usePermitToActionFeature([CRUDAction.Update], FeatureKey.Cycle)
  const readOnly = isEditMode && !showUpdateBtn

  const selectedBreedName = breeds?.find((b) => b.id === watch('breed'))?.name ?? ''
  const isSeabass = selectedBreedName === 'ปลากะพง' || selectedBreedName === 'ปลากะพงยักษ์'
  const { refetch } = useGetPondNameById(pondId || '')

  const [dialogConfig, setDialogConfig] = useState<DialogConfig>({
    type: 'totalProduction',
    mode: 'create',
    isOpen: false,
  })
  // Only flip isOpen so the dialog doesn't switch to "create" while fading out.
  const closeDialog = () => setDialogConfig((config) => ({ ...config, isOpen: false }))
  // Controlled so the menu closes when the pencil or "+ create" opens the formula dialog.
  const [isProductionMenuOpen, setIsProductionMenuOpen] = useState(false)
  const canManageFeedProgram = !readOnly
  // "+ สร้างสูตรประเมิน" is behind the CUSTOM_YIELD_FORMULA feature flag
  const { data: isCustomYieldFormulaEnabled } = useFeatureEnabled('CUSTOM_YIELD_FORMULA')

  useEffect(() => {
    if (cycleInfo && isEditMode) {
      reset((values) => ({
        ...values,
        startDate: parseISO(cycleInfo.startDate),
        endDate: cycleInfo.endDate ? parseISO(cycleInfo.endDate) : undefined,
        saltiness: cycleInfo.salinity,
        breed: cycleInfo.breedProduct.breedId,
        breedBrand: cycleInfo.breedProduct.breedBrandId,
        breedProduct: cycleInfo.breedProduct.id,
        amount: cycleInfo.initialShrimpCount,
        totalProduction: cycleInfo.feedProgramId ?? -1,
        totalMarket: cycleInfo.marketId ?? -1,
        feedPercentOfFishWeight: cycleInfo.feedPercentOfFishWeight
          ? fNumber(cycleInfo.feedPercentOfFishWeight)
          : undefined,
        initialSize: cycleInfo.initialSize ? fNumber(cycleInfo.initialSize) : undefined,
      }))
    }
  }, [cycleInfo])

  useEffect(() => {
    const totalProductionValue = watch('totalProduction')
    const totalMarketValue = watch('totalMarket')

    if (speciesName === SpeciesName.Shrimp) {
      if (totalProductionValue === -1 || totalProductionValue === null) {
        const defaultProgramId = totalProductions?.find((program) => program.isDefault)?.id
        if (defaultProgramId) {
          setValue('totalProduction', defaultProgramId)
        }
      }

      if (totalMarketValue === -1 || totalMarketValue === null) {
        const defaultMarketId = totalMarkets?.find((program) => program.isDefault)?.id
        if (defaultMarketId) {
          setValue('totalMarket', defaultMarketId)
        }
      }
    }
  }, [totalProductions, totalMarkets])

  useEffect(() => {
    if (isFish && !isEditMode) {
      setValue('feedPercentOfFishWeight', '3.00')
    }
  }, [isFish])

  useEffect(() => {
    if (isFish && breedProduct && breedProduct.length > 0 && getValues('breedProduct') === -1) {
      setValue('breedProduct', breedProduct[0].id)
    }
  }, [breedProduct, isFish])

  const onSubmit = async (data: FormDataType) => {
    const cycleBodyRequest = {
      salinity: Number(data.saltiness),
      shrimpTypeId: data.breedProduct,
      shrimpBrandId: data.breedBrand,
      initialShrimpCount: Number(data.amount),
      startDate: format(data.startDate, 'yyyy-MM-dd'),
      endDate: data.endDate ? format(data.endDate, 'yyyy-MM-dd') : '',
      breedProductId: data.breedProduct,
      feedProgramId: data.totalProduction !== -1 ? data.totalProduction : null,
      marketId: data.totalMarket !== -1 ? data.totalMarket : null,
      feedPercentOfFishWeight: data.feedPercentOfFishWeight
        ? Number(data.feedPercentOfFishWeight)
        : undefined,
      initialSize: data.initialSize ? Number(data.initialSize) : undefined,
    }

    // edit cycle
    if (isEditMode && cycleInfo && pondInfo) {
      await editCycleMutation
        .mutateAsync({
          body: cycleBodyRequest,
        })
        .then(() => {
          enqueueSnackbar(t('common:save.success'), { variant: 'success' })
        })

      return
    }

    // create cycle
    const mutation = creditWalletEnabled ? createCycleWithCreditMutation : createCycleMutation
    await mutation
      .mutateAsync({
        body: cycleBodyRequest,
      })
      .finally(() => {
        refetch()
        onClose?.()
      })
  }

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const isSubmitting =
    createCycleMutation.isLoading ||
    createCycleWithCreditMutation.isLoading ||
    editCycleMutation.isLoading

  useEffect(() => {
    onSubmittingChange?.(isSubmitting)
  }, [isSubmitting, onSubmittingChange])

  const getMenuItems = ({
    loading,
    items,
    type = 'totalProduction',
  }: {
    loading: boolean
    items: { id: number; name: string; isEditable?: boolean }[] | undefined
    type?: EstimateDialogType
  }) => {
    if (loading) return <Typography>Loading...</Typography>

    const onEdit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
      // Keep the click from reaching the MenuItem, which would select this option.
      e.preventDefault()
      e.stopPropagation()
      setIsProductionMenuOpen(false)

      setDialogConfig({
        mode: 'edit',
        type: type,
        initialId: index,
        isOpen: true,
      })
    }

    return items?.map((el) => (
      <MenuItem key={el.id} value={el.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {el.name}
          {el.isEditable && canManageFeedProgram && (
            <IconButton size="small" sx={{ ml: 1 }} onClick={(e) => onEdit(e, el.id)}>
              <Icon icon={editFill} />
            </IconButton>
          )}
        </Box>
      </MenuItem>
    ))
  }

  const shrimpSetting = {
    category: t('pond:evaluationFormula'),
    list: [
      {
        label: `${t('pond:total.production')}`,
        component: (
          <Controller
            name="totalProduction"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  select
                  size="small"
                  error={!!errors.totalProduction}
                  helperText={errorMessageByField('totalProduction', errors)}
                  onChange={(e) => {
                    const value = e.target.value
                    setValue('totalProduction', parseInt(value), { shouldDirty: true })
                  }}
                  disabled={readOnly}
                >
                  <MenuItem value={-1} disabled>
                    {t('pond:select.totalProduction')}
                  </MenuItem>

                  {getMenuItems({
                    type: 'totalProduction',
                    loading: isTotalProductionsLoading,
                    items: totalProductions?.map((item) => ({
                      id: item.id,
                      name: item.programName,
                      isEditable: item.editable,
                    })),
                  })}

                  {showUpdateBtn && (
                    <Box sx={{ paddingX: '16px', paddingY: '6px' }}>
                      <Button
                        sx={{ alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => {
                          setDialogConfig({
                            type: 'totalProduction',
                            mode: 'create',
                            isOpen: true,
                          })
                        }}
                      >
                        <Icon icon={plus} />
                        {t('pond:create')}
                        {t('pond:program')}
                      </Button>
                    </Box>
                  )}
                </TextField>
              </>
            )}
          />
        ),
      },
      // {
      //   label: `${t('pond:income')}`,
      //   component: (
      //     <Controller
      //       name="totalMarket"
      //       control={control}
      //       render={({ field }) => (
      //         <>
      //           <TextField
      //             {...field}
      //             fullWidth
      //             select
      //             size="small"
      //             error={!!errors.totalMarket}
      //             helperText={errorMessageByField('totalMarket', errors)}
      //             onChange={(e) => {
      //               const value = e.target.value
      //               setValue('totalMarket', parseInt(value), { shouldDirty: true })
      //             }}
      //           >
      //             <MenuItem value={-1} disabled>
      //               {t('pond:select.totalMarket')}
      //             </MenuItem>

      //             {getMenuItems({
      //               loading: isTotalMarketsLoading,
      //               type: 'totalMarket',
      //               items: totalMarkets?.map((item) => ({
      //                 id: item.id,
      //                 name: item.marketName,
      //                 isEditable: item.editable,
      //               })),
      //             })}

      //             <Box sx={{ paddingX: '16px', paddingY: '6px' }}>
      //               <Button
      //                 sx={{ alignItems: 'center', justifyContent: 'center', gap: '8px' }}
      //                 variant="contained"
      //                 size="small"
      //                 fullWidth
      //                 onClick={() => {
      //                   const totalMarketValue = watch('totalMarket', 0)

      //                   const breedId = totalMarkets?.find(
      //                     (item) => item.id === totalMarketValue
      //                   )?.breedId
      //                   setDialogConfig({
      //                     type: 'totalMarket',
      //                     mode: 'create',
      //                     isOpen: true,
      //                     programId: breedId,
      //                   })
      //                 }}
      //               >
      //                 <Icon icon={plus} />
      //                 {t('pond:create')}
      //                 {t('pond:program')}
      //               </Button>
      //             </Box>
      //           </TextField>
      //         </>
      //       )}
      //     />
      //   ),
      // },
    ],
  }

  const fishSetting = {
    category: t('pond:evaluationFormula'),
    list: [
      {
        label: t('pond:feed.consume'),
        component: (
          <Controller
            name="feedPercentOfFishWeight"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                error={!!errors.feedPercentOfFishWeight}
                helperText={errorMessageByField('feedPercentOfFishWeight', errors)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('fish.weight')}</InputAdornment>,
                }}
                disabled={readOnly}
              />
            )}
          />
        ),
      },
    ],
  }

  // const formulaSetting = isFish ? fishSetting : shrimpSetting

  const SETTINGS = [
    {
      // INFORMATION
      category: t('pond:cycle.information'),
      list: [
        {
          label: t('pond:start.feeding.date'),
          component: (
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <ThaiDatePicker
                  {...field}
                  format="dd/MM/yyyy"
                  maxDate={endOfToday()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.startDate,
                      helperText: errorMessageByField('startDate', errors),
                      fullWidth: true,
                      disabled: readOnly,
                    },
                  }}
                  disabled={readOnly}
                />
              )}
            />
          ),
        },
        ...(isEditMode && cycleInfo?.endDate
          ? [
              {
                label: t('pond:end.cycle.date'),
                component: (
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <ThaiDatePicker
                        {...field}
                        value={field.value ?? null}
                        maxDate={endOfToday()}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!errors.endDate,
                            helperText: errorMessageByField('endDate', errors),
                            fullWidth: true,
                            disabled: readOnly,
                          },
                        }}
                        disabled={readOnly}
                      />
                    )}
                  />
                ),
              },
            ]
          : []),
      ],
    },
    {
      // SETTING
      category: t('pond:cycle.setting'),
      list: [
        {
          label: t('pond:saltiness'),
          component: (
            <Controller
              name="saltiness"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  type="text"
                  error={!!errors.saltiness}
                  helperText={errorMessageByField('saltiness', errors)}
                  disabled={readOnly}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{'ppt'}</InputAdornment>,
                  }}
                />
              )}
            />
          ),
        },
        {
          label: `${t('pond:breed')}${speciesName}`,
          component: (
            <Controller
              name="breed"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  select
                  size="small"
                  error={!!errors.breed}
                  helperText={errorMessageByField('breed', errors)}
                  onChange={(e) => {
                    const value = e.target.value
                    setValue('breed', parseInt(value))
                    setValue('breedBrand', -1)
                    setValue('breedProduct', -1)
                  }}
                  disabled={readOnly}
                >
                  <MenuItem value={-1} disabled>
                    {t('pond:select.breed')}
                  </MenuItem>
                  {getMenuItems({ loading: isBreedLoading, items: breeds })}
                </TextField>
              )}
            />
          ),
        },
        ...(watch('breed') !== -1
          ? [
              {
                label: `${t('pond:breed.brand')}${speciesName}`,
                component: (
                  <Controller
                    name="breedBrand"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        select
                        size="small"
                        error={!!errors.breedBrand}
                        helperText={errorMessageByField('breedBrand', errors)}
                        disabled={readOnly}
                      >
                        <MenuItem value={-1} disabled>
                          {t('pond:select.breed.brand')}
                        </MenuItem>
                        {getMenuItems({ loading: isBreedBrandLoading, items: breedBrand })}
                      </TextField>
                    )}
                  />
                ),
              },
            ]
          : []),
        ...(isFish && watch('breed') !== -1
          ? [
              {
                label: t('pond:initial.size'),
                component: (
                  <Controller
                    name="initialSize"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        error={!!errors.initialSize}
                        helperText={errorMessageByField('initialSize', errors)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{t('pond:gram.per.one')}</InputAdornment>
                          ),
                        }}
                        disabled={readOnly}
                      />
                    )}
                  />
                ),
              },
            ]
          : []),
        ...(!isFish && watch('breedBrand') !== -1
          ? [
              {
                label: `${t('pond:breed.product')}${speciesName}`,
                component: (
                  <Controller
                    name="breedProduct"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        select
                        size="small"
                        error={!!errors.breedProduct}
                        helperText={errorMessageByField('breedProduct', errors)}
                        disabled={readOnly}
                      >
                        <MenuItem value={-1} disabled>
                          {`${t('pond:select.breed.brand')}${speciesName}`}
                        </MenuItem>
                        {getMenuItems({
                          loading: isBreedProductLoading,
                          items: breedProduct?.map((el) => ({ id: el.id, name: el.productName })),
                        })}
                      </TextField>
                    )}
                  />
                ),
              },
            ]
          : []),
        {
          label: t('pond:amount'),
          component: (
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <IntegerTextField
                  field={field}
                  error={!!errors.amount}
                  helperText={errorMessageByField('amount', errors)}
                  endAdornment={
                    <InputAdornment position="end">{t('pond:shrimp.unit')}</InputAdornment>
                  }
                  disabled={readOnly}
                />
              )}
            />
          ),
        },
        ...(isFish
          ? [
              ...(!isSeabass
                ? [
                    {
                      label: t('pond:feed.consume'),
                      component: (
                        <Controller
                          name="feedPercentOfFishWeight"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              error={!!errors.feedPercentOfFishWeight}
                              helperText={errorMessageByField('feedPercentOfFishWeight', errors)}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">{t('fish.weight')}</InputAdornment>
                                ),
                              }}
                              disabled={readOnly}
                            />
                          )}
                        />
                      ),
                    },
                  ]
                : []),
            ]
          : [
              {
                label: `${t('pond:total.production')}`,
                component: (
                  <Controller
                    name="totalProduction"
                    control={control}
                    render={({ field }) => (
                      <>
                        <TextField
                          {...field}
                          fullWidth
                          select
                          size="small"
                          error={!!errors.totalProduction}
                          helperText={errorMessageByField('totalProduction', errors)}
                          onChange={(e) => {
                            const value = parseInt(e.target.value)
                            if (value === CREATE_FEED_PROGRAM_OPTION) {
                              setDialogConfig({
                                type: 'totalProduction',
                                mode: 'create',
                                isOpen: true,
                              })
                              return
                            }
                            setValue('totalProduction', value, { shouldDirty: true })
                          }}
                          disabled={readOnly}
                          SelectProps={{
                            open: isProductionMenuOpen,
                            onOpen: () => setIsProductionMenuOpen(true),
                            onClose: () => setIsProductionMenuOpen(false),
                            // Cap the height so long lists scroll inside the menu, with the
                            // "+ create" row pinned to the bottom (sticky footer below).
                            MenuProps: {
                              PaperProps: { sx: { maxHeight: 320 } },
                              MenuListProps: { sx: { pb: 0 } },
                            },
                            // Show the name only — the option itself also holds the pencil button.
                            renderValue: (value) =>
                              value === -1
                                ? t('pond:select.totalProduction')
                                : totalProductions?.find((p) => p.id === value)?.programName ?? '',
                          }}
                        >
                          <MenuItem value={-1} disabled>
                            {t('pond:select.totalProduction')}
                          </MenuItem>

                          {getMenuItems({
                            type: 'totalProduction',
                            loading: isTotalProductionsLoading,
                            items: totalProductions?.map((item) => ({
                              id: item.id,
                              name: item.programName,
                              isEditable: item.editable,
                            })),
                          })}

                          {canManageFeedProgram && isCustomYieldFormulaEnabled && (
                            <MenuItem
                              value={CREATE_FEED_PROGRAM_OPTION}
                              sx={{
                                mt: 0.5,
                                py: 1.25,
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 1,
                                // Opaque so options scrolling underneath don't show through.
                                bgcolor: theme.palette.background.paper,
                                '&:hover, &.Mui-focusVisible': { bgcolor: theme.palette.grey[100] },
                                borderTop: `1px solid ${theme.palette.divider}`,
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                gap: 1,
                              }}
                            >
                              <Icon icon={plus} />
                              {t('pond:feedProgram.create.action')}
                            </MenuItem>
                          )}
                        </TextField>
                      </>
                    )}
                  />
                ),
              },
            ]),
      ],
    },
  ]

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} style={{ minWidth: '400px' }}>
      {isEditMode && showUpdateBtn && showSaveButton && (
        <Box sx={{ position: 'absolute', right: '24px', top: '24px' }}>
          <LoadingButton
            disabled={!isDirty}
            variant="contained"
            color="primary"
            type="submit"
            form="setting-pond"
          >
            {t('common:save')}
          </LoadingButton>
        </Box>
      )}
      <Stack spacing={3}>
        {SETTINGS.map((item) => (
          <Fragment key={item.category}>
            <Typography fontSize={18} fontWeight={600} color={theme.palette.primary.dark}>
              {item.category}
            </Typography>
            {item.list.map((sublist) => (
              <Stack key={sublist.label} direction="column" alignItems="flex-start" gap={2}>
                <Typography fontSize={16} fontWeight={600}>
                  <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                    *
                  </Typography>
                  {sublist.label}
                </Typography>
                <Box width="100%">
                  {cycleFetching ? (
                    <Skeleton animation="wave" variant="text" width="100%" />
                  ) : (
                    sublist.component
                  )}
                </Box>
              </Stack>
            ))}
          </Fragment>
        ))}
      </Stack>
      {dialogConfig.type === 'totalProduction' && (
        <FeedProgramDialog
          open={dialogConfig.isOpen}
          feedProgramId={dialogConfig.mode === 'edit' ? dialogConfig.initialId : undefined}
          onClose={closeDialog}
          onCreated={(id) => setValue('totalProduction', id, { shouldDirty: true })}
          onDeleted={(id) => {
            if (getValues('totalProduction') !== id) return
            const defaultId = totalProductions?.find((p) => p.isDefault && p.id !== id)?.id
            setValue('totalProduction', defaultId ?? -1, { shouldDirty: true })
          }}
        />
      )}
    </form>
  )
}

export default PondCycleForm
